/*
 * 1DS JS SDK Authentication plugin, 3.2.18
 * Copyright (c) Microsoft and contributors. All rights reserved.
 * (Microsoft Internal Only)
 */
import { __extendsFn as __extends } from "@microsoft/applicationinsights-shims";
/**
* AuthPlugin.ts
* @author Hector Hernandez (hectorh)
* @copyright Microsoft 2019
*/
import dynamicProto from "@microsoft/dynamicproto-js";
import { BaseTelemetryPlugin, _throwInternal, addPageHideEventListener, addPageUnloadEventListener, arrForEach, createUniqueNamespace, eventOff, eventOn, getCookieValue, getDocument, getISOString, getWindow, mergeEvtNamespace, objDefineAccessors, removePageHideEventListener, removePageUnloadEventListener, safeGetCookieMgr, setProcessTelemetryTimings } from "@microsoft/1ds-core-js";
import { AuthType } from "./Enums";
import { WebAuthHandler } from "./WebAuthHandler";
var bouncedAuthCookieName = "authBounced";
var aadloginUrl = "https://login.microsoftonline.com";
var defaultHandShakeTimeoutMS = 5000;
var AuthPlugin = /** @class */ (function (_super) {
    __extends(AuthPlugin, _super);
    function AuthPlugin() {
        var _this = _super.call(this) || this;
        _this.identifier = "AuthPlugin";
        _this.priority = 190;
        _this.version = '3.2.18';
        var _core;
        var _authHandler;
        var _authConfig;
        var _handshakeInProgress;
        var _authHandhshakeTimeout;
        var _authHandhshakeTimeoutMs;
        var _eventQueue;
        var _evtNamespace;
        dynamicProto(AuthPlugin, _this, function (_self, _base) {
            _initDefaults();
            _self.initialize = function (coreConfig, core, extensions) {
                var _self = _this;
                _core = core;
                _super.prototype.initialize.call(_this, coreConfig, core, extensions);
                _evtNamespace = mergeEvtNamespace(createUniqueNamespace(_self.identifier), core.evtNamespace && core.evtNamespace());
                var identifier = _self.identifier;
                // Make sure we have a config definition for this identifier
                _authConfig = coreConfig.extensionConfig[identifier] || {};
                coreConfig.extensionConfig[identifier] = _authConfig;
                _handshakeInProgress = false;
                _authHandhshakeTimeoutMs = _authConfig.handShakeTimeoutMs ? _authConfig.handShakeTimeoutMs : defaultHandShakeTimeoutMS;
                // Override endpointUrl if provided in config
                var endpointUrl = _authConfig.overrideAuthEndpointUrl ? _authConfig.overrideAuthEndpointUrl : coreConfig.endpointUrl;
                _authHandler = new WebAuthHandler(endpointUrl, _authConfig.authType, _self.diagLog());
                _updateWParam();
                function _doUnload() {
                    _releaseEventQueue();
                }
                var excludePageUnloadEvents = coreConfig.disablePageUnloadEvents || [];
                // If page is closed release queue
                addPageUnloadEventListener(_doUnload, excludePageUnloadEvents, _evtNamespace);
                addPageHideEventListener(_doUnload, excludePageUnloadEvents, _evtNamespace);
                // Add listener to receive messages from Collector to determine when auth handshake is done
                // This message will be sent from iframe to parent window
                var win = getWindow();
                if (win) {
                    eventOn(win, "message", function (msg) { _receiveMessage(msg, endpointUrl); }, _evtNamespace);
                }
            };
            _self.processTelemetry = function (event, itemCtx) {
                itemCtx = _self._getTelCtx(itemCtx);
                setProcessTelemetryTimings(event, _self.identifier);
                // User is logged in
                if (_authConfig.authType !== AuthType.NONE && _authConfig.loggedInStatusCallback && _authConfig.loggedInStatusCallback() === true) {
                    var cookieMgr = safeGetCookieMgr(_self.core);
                    // Check for bounced cookie
                    var authBounced = getCookieValue(cookieMgr, bouncedAuthCookieName);
                    if (!authBounced) {
                        // Create bounced cookie to avoid multiple handshake processes
                        var halfHour = 30 * 60;
                        cookieMgr.set(bouncedAuthCookieName, getISOString(new Date()), halfHour);
                        // Start hand shake process
                        _handshakeInProgress = true;
                        // Timeout handshake if it takes more than 5 seconds
                        _authHandhshakeTimeout = setTimeout(function () { _completeAuthHandshake(); }, _authHandhshakeTimeoutMs);
                        _authHandler.startAuthHandshake();
                    }
                }
                if (_handshakeInProgress) {
                    _eventQueue.push({
                        ctx: itemCtx,
                        item: event
                    });
                }
                else {
                    _self.processNext(event, itemCtx);
                }
            };
            _self.signOut = function () {
                // Clean client and server side cookies
                var cookieMgr = safeGetCookieMgr(_core);
                cookieMgr.del(bouncedAuthCookieName);
                _authHandler.signOutAndClearCookies();
                _completeAuthHandshake();
            };
            _self.setAuthType = function (authType) {
                // Comple handshake if triggered before
                _completeAuthHandshake();
                _authConfig.authType = authType;
                _authHandler.setAuthType(authType);
                _updateWParam();
            };
            _self._doTeardown = function (unloadCtx, unloadState) {
                _completeAuthHandshake();
                eventOff(getWindow(), "message", null, _evtNamespace);
                removePageUnloadEventListener(null, _evtNamespace);
                removePageHideEventListener(null, _evtNamespace);
                // Just register to remove all events associated with this namespace
                _initDefaults();
            };
            /**
             * Add w parameter used by Collector to identify Web Requests will try to get auth properties
             */
            function _updateWParam() {
                if (_authConfig.authType != AuthType.NONE) {
                    var extCore = _core;
                    var existingGetWParamMethod = extCore.getWParam;
                    extCore.getWParam = function () {
                        var wparam = 0;
                        wparam = _authConfig.authType === AuthType.MSA ? wparam | 8 : wparam | 16;
                        return wparam | (existingGetWParamMethod ? existingGetWParamMethod() : 0);
                    };
                }
            }
            /**
             * Attaches to message, to receive responses from Collector.
             */
            function _receiveMessage(event, endpointUrl) {
                var doc = getDocument();
                if (doc) {
                    var a = doc.createElement("a");
                    a.href = endpointUrl;
                    var endpointOrigin = a.protocol + "//" + a.hostname;
                    // login.microsoftonline.com is workaround for IE10 where the origin is the iframe of origin not origin of request
                    if (event.origin === endpointOrigin || event.origin === aadloginUrl) {
                        if (event.data) {
                            try {
                                _completeAuthHandshake();
                            }
                            catch (e) {
                                _throwInternal(_self.diagLog(), 1 /* eLoggingSeverity.CRITICAL */, 502 /* _eExtendedInternalMessageId.AuthRedirectFail */, "Error receiving auth redirect message: " + e);
                            }
                        }
                    }
                }
            }
            /**
            * Complete auth handhshake, cleanup and release the event queue
            */
            function _completeAuthHandshake() {
                if (_authHandhshakeTimeout) {
                    clearTimeout(_authHandhshakeTimeout);
                }
                if (_authHandler) {
                    // Remove auth iFrame if present
                    _authHandler.cleanAuthIFrame();
                }
                _handshakeInProgress = false;
                _releaseEventQueue();
            }
            /**
            * Release internal event queue
            */
            function _releaseEventQueue() {
                arrForEach(_eventQueue, function (evt) {
                    evt.ctx.processNext(evt.item);
                });
                _eventQueue = [];
            }
            function _initDefaults() {
                _core = null;
                _authHandler = null;
                _authConfig = null;
                _handshakeInProgress = false;
                _authHandhshakeTimeout = null;
                _authHandhshakeTimeoutMs = null;
                _eventQueue = [];
            }
            // For backward compatibility
            objDefineAccessors(_self, "_authHandler", function () { return _authHandler; });
            objDefineAccessors(_self, "_authConfig", function () { return _authConfig; });
            objDefineAccessors(_self, "_eventQueue", function () { return _eventQueue; });
            objDefineAccessors(_self, "_handshakeInProgress", function () { return _handshakeInProgress; });
            objDefineAccessors(_self, "_evtNamespace", function () { return "." + _evtNamespace; });
        });
        return _this;
    }
// Removed Stub for AuthPlugin.prototype.initialize.
// Removed Stub for AuthPlugin.prototype.processTelemetry.
// Removed Stub for AuthPlugin.prototype.signOut.
// Removed Stub for AuthPlugin.prototype.setAuthType.
    // This is a workaround for an IE8 bug when using dynamicProto() with classes that don't have any
    // non-dynamic functions or static properties/functions when using uglify-js to minify the resulting code.
    // this will be removed when ES3 support is dropped.
    AuthPlugin.__ieDyn=1;

    return AuthPlugin;
}(BaseTelemetryPlugin));
export { AuthPlugin };
//# sourceMappingURL=AuthPlugin.js.map